import type {
  HandbookDocument,
  HandbookFill,
  HandbookFillResults,
} from './handbookContract';
import { handbookErrorMessage } from './handbookTree';

/** How often to ask whether uploaded documents have been read, or a fill has finished. */
export const DOCUMENT_POLL_INTERVAL_MS = 2000;
export const FILL_POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 10 * 60 * 1000;

/** A fill or document problem with a message fit to show the admin. */
export class HandbookFillError extends Error {}

export const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const unreadable = (doc: HandbookDocument) =>
  doc.status !== 'pending' && doc.status !== 'extracted';

const describe = (doc: HandbookDocument) =>
  doc.status === 'expired'
    ? `“${doc.title}” is no longer available; upload it again to use it.`
    : doc.error
      ? `“${doc.title}” could not be read: ${doc.error}`
      : `“${doc.title}” could not be read.`;

/**
 * Waits until none of the given documents is still being read, then returns
 * the ones with usable text. Throws when none of them can be used, naming
 * the first that could not be read.
 */
export async function awaitDocuments(
  ids: string[],
  list: () => Promise<HandbookDocument[]>,
): Promise<HandbookDocument[]> {
  if (ids.length === 0) return [];
  const started = Date.now();
  for (;;) {
    const docs = await list();
    const mine = ids
      .map((id) => docs.find((d) => d.id === id))
      .filter((d): d is HandbookDocument => !!d);
    const pending = mine.filter((d) => d.status === 'pending');
    if (pending.length === 0) {
      const usable = mine.filter((d) => d.status === 'extracted');
      if (usable.length === 0) {
        const bad = mine.find(unreadable);
        throw new HandbookFillError(
          bad ? describe(bad) : 'Your documents could not be found.',
        );
      }
      return usable;
    }
    if (Date.now() - started > MAX_WAIT_MS) {
      throw new HandbookFillError(
        'Your documents are taking too long to read. Try again in a moment.',
      );
    }
    await sleep(DOCUMENT_POLL_INTERVAL_MS);
  }
}

/** Polls a queued fill until it is done, returning its results; throws when it failed. */
export async function awaitFill(
  fill: HandbookFill,
  get: (fillId: string) => Promise<HandbookFill>,
): Promise<HandbookFillResults> {
  let current = fill;
  const started = Date.now();
  while (current.status === 'queued' || current.status === 'running') {
    if (Date.now() - started > MAX_WAIT_MS) {
      throw new HandbookFillError(
        'Writing your pages is taking too long. Try again in a moment.',
      );
    }
    await sleep(FILL_POLL_INTERVAL_MS);
    current = await get(current.id);
  }
  if (current.status !== 'done' || !current.results) {
    throw new HandbookFillError(
      current.error ?? 'Your pages could not be written. Try again.',
    );
  }
  return current.results;
}

export function fillErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof HandbookFillError) return err.message;
  return handbookErrorMessage(err, fallback);
}
